import { useEffect, useMemo, useState } from "react";
import { api, fmt, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { PageHeader } from "@/components/EntityPage";

function AssignmentForm({ initial, refs, driverId, onSubmit }) {
  const [f, setF] = useState(initial || { driver_id: driverId, status: "upcoming" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">Trip title</Label>
          <Input value={f.trip_title || ""} onChange={(e) => set("trip_title", e.target.value)} data-testid="asg-title" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Trip date</Label>
          <Input type="date" value={f.trip_date || ""} onChange={(e) => set("trip_date", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">End date</Label>
          <Input type="date" value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Client</Label>
          <Select value={f.client_id || ""} onValueChange={(v) => set("client_id", v)}>
            <SelectTrigger data-testid="asg-client" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Select client"/></SelectTrigger>
            <SelectContent>{refs.clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Vehicle</Label>
          <Select value={f.vehicle_id || ""} onValueChange={(v) => set("vehicle_id", v)}>
            <SelectTrigger className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Vehicle"/></SelectTrigger>
            <SelectContent>{refs.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_no}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">Itinerary</Label>
          <Textarea rows={4} value={f.itinerary || ""} onChange={(e) => set("itinerary", e.target.value)} data-testid="asg-itinerary" placeholder="Day 1: Pickup at 6am from Salt Lake…&#10;Day 2: Darjeeling Mall Road, tea garden visit…" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div className="col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">Notes</Label>
          <Input value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Status</Label>
          <Select value={f.status || "upcoming"} onValueChange={(v) => set("status", v)}>
            <SelectTrigger className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="ongoing">Ongoing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end"><Button type="submit" data-testid="asg-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Save</Button></div>
    </form>
  );
}

export default function DriverAdmin() {
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [driverId, setDriverId] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function loadRefs() {
    const [d, c, v] = await Promise.all([api.get("/drivers"), api.get("/clients"), api.get("/vehicles")]);
    setDrivers(d.data); setClients(c.data); setVehicles(v.data);
    if (d.data.length && !driverId) setDriverId(d.data[0].id);
  }
  async function loadAll() {
    const [a, w] = await Promise.all([
      api.get(driverId ? `/driver-assignments?driver_id=${driverId}` : "/driver-assignments"),
      api.get("/withdrawals"),
    ]);
    setAssignments(a.data);
    setWithdrawals(w.data);
  }
  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { if (driverId) loadAll(); }, [driverId]);

  async function saveAssignment(f) {
    try {
      const payload = { ...f, driver_id: driverId };
      if (editing) await api.put(`/driver-assignments/${editing.id}`, payload);
      else await api.post("/driver-assignments", payload);
      toast.success("Saved"); setOpen(false); setEditing(null); loadAll();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }
  async function delAssignment(id) {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/driver-assignments/${id}`); loadAll(); toast.success("Deleted"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }
  async function decide(w, approve) {
    try { await api.post(`/withdrawals/${w.id}/approve`, { approve, note: "" });
      toast.success(approve ? "Approved — driver balance updated" : "Rejected"); loadAll();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }

  const clientOf = (id) => clients.find((c) => c.id === id);
  const vehicleOf = (id) => vehicles.find((v) => v.id === id);
  const pendingWD = withdrawals.filter((w) => (w.status || "pending") === "pending");
  const decidedWD = withdrawals.filter((w) => w.status && w.status !== "pending");

  const statusTone = {
    approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    rejected: "bg-red-500/15 text-red-300 border-red-500/40",
    pending: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  };

  return (
    <div>
      <PageHeader title="Driver Admin"
        subtitle="Assign upcoming trips with itinerary and client details. Approve or reject driver withdrawal requests."
        right={
          <>
            <div className="w-64">
              <Select value={driverId} onValueChange={setDriverId}>
                <SelectTrigger data-testid="da-driver" className="bg-[hsl(224_40%_12%)]"><SelectValue placeholder="Select driver"/></SelectTrigger>
                <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button disabled={!driverId} data-testid="da-add" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4"/> New assignment</Button>
              </DialogTrigger>
              <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-2xl">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit assignment" : "New assignment"}</DialogTitle></DialogHeader>
                <AssignmentForm initial={editing} refs={{ clients, vehicles }} driverId={driverId} onSubmit={saveAssignment}/>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      {/* Pending withdrawals */}
      {pendingWD.length > 0 && (
        <Card className="p-5 bg-amber-500/[0.06] border-amber-500/40 mb-6" data-testid="pending-withdrawals">
          <div className="flex items-center gap-2 mb-3 text-amber-300"><Clock className="w-4 h-4"/><div className="text-xs uppercase tracking-[0.2em]">Pending withdrawal approvals</div>
            <Badge className="ml-auto bg-amber-500/20 text-amber-200 border-amber-500/40">{pendingWD.length}</Badge>
          </div>
          <div className="space-y-2">
            {pendingWD.map((w) => (
              <div key={w.id} data-testid={`wd-pending-${w.id}`} className="flex items-center gap-3 flex-wrap px-3 py-2 rounded-md border border-amber-500/30 bg-[hsl(224_45%_9%)]">
                <div className="min-w-[140px]"><div className="text-[10px] uppercase text-muted-foreground">Driver</div><div>{w.driver_name || "—"}</div></div>
                <div className="min-w-[100px]"><div className="text-[10px] uppercase text-muted-foreground">Amount</div><div className="font-mono text-amber-300">{fmt(w.amount)}</div></div>
                <div className="flex-1 min-w-[160px]"><div className="text-[10px] uppercase text-muted-foreground">Note</div><div className="text-xs text-slate-300">{w.note || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground">Requested</div><div className="text-xs font-mono">{w.created_at?.slice(0,10)}</div></div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`approve-${w.id}`} onClick={() => decide(w, true)} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 gap-2"><CheckCircle2 className="w-3.5 h-3.5"/> Approve</Button>
                  <Button size="sm" variant="outline" data-testid={`reject-${w.id}`} onClick={() => decide(w, false)} className="border-red-500/40 text-red-300 hover:bg-red-500/10 gap-2"><XCircle className="w-3.5 h-3.5"/> Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Assignments list */}
      <Card className="p-5 bg-[hsl(224_45%_9%)] border-border mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-3">Assignments · {drivers.find((d) => d.id === driverId)?.name || "no driver"}</div>
        {assignments.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No assignments yet for this driver.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {assignments.map((a) => {
              const c = clientOf(a.client_id);
              const v = vehicleOf(a.vehicle_id);
              return (
                <div key={a.id} data-testid={`admin-asg-${a.id}`} className="p-4 rounded-lg border border-border bg-[hsl(224_40%_11%)]">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="font-semibold">{a.trip_title || "Trip"}</div>
                    <Badge className="ml-auto bg-amber-500/15 text-amber-300 border-amber-500/40 uppercase text-[10px]">{a.status}</Badge>
                  </div>
                  <div className="text-xs grid grid-cols-2 gap-2">
                    <div><div className="text-[10px] uppercase text-muted-foreground">Dates</div><div className="font-mono">{a.trip_date || "—"}{a.end_date ? ` → ${a.end_date}` : ""}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Client</div><div>{c?.name || "—"}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Vehicle</div><div className="font-mono">{v?.registration_no || "—"}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Contact</div><div className="font-mono text-emerald-300">{c?.whatsapp || c?.mobile || "—"}</div></div>
                  </div>
                  {a.itinerary && <div className="mt-2 text-[11px] text-muted-foreground whitespace-pre-line line-clamp-4">{a.itinerary}</div>}
                  <div className="mt-3 flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(a); setOpen(true); }}><Pencil className="w-3.5 h-3.5"/></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-red-400" onClick={() => delAssignment(a.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Withdrawal history */}
      {decidedWD.length > 0 && (
        <Card className="p-5 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-3">Withdrawal history</div>
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
              {["Driver","Amount","Note","Status","Decided"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {decidedWD.map((w) => (
                <tr key={w.id} className="border-t border-border">
                  <td className="p-3">{w.driver_name || "—"}</td>
                  <td className="p-3 font-mono text-amber-300">{fmt(w.amount)}</td>
                  <td className="p-3 text-xs text-muted-foreground">{w.note || "—"}</td>
                  <td className="p-3"><Badge className={statusTone[w.status]}>{w.status}</Badge></td>
                  <td className="p-3 text-xs font-mono">{w.decided_at?.slice(0,10) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
